import { baseApi } from "common/redux/core/api.base";
import { ProcessingHubService } from "../../services/processing-hub.service";
import {
  SearchDocumentsRequest,
  DocumentListResponse,
  Document,
  PaginatedDocumentResponse,
  FlowConfiguration,
  ConfigurationDetails,
  GdxDocumentDetail,
  DocumentTypeInformation,
  GdxPaginatedResponse,
  BatchPaginatedResponse
} from "../../models/processing-hub.model";
import { ApiCacheTags } from "common/models/api-base.model";

const processingHubQueriesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDocuments: build.query<DocumentListResponse, SearchDocumentsRequest>({
      queryFn: (searchParams: SearchDocumentsRequest) => {
        return ProcessingHubService.getDocuments(searchParams);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getUploadedDocuments: build.query<
      PaginatedDocumentResponse,
      {
        hasDuplicate?: boolean;
        page?: number;
        size?: number;
        sort?: string;
        dir?: string;
        search?: string;
        showDuplicates?: boolean;
        useEngApi?: boolean;
        flowId?: number | string;
      } | void
    >({
      queryFn: async (params) => {
        // Ensure we have a proper params object
        const queryParams = params || {};
        const {
          hasDuplicate = false,
          page = 1,
          size = 10,
          sort = "",
          dir = "",
          search = "",
          showDuplicates = false,
          useEngApi = false,
          flowId
        } = queryParams;

        // Create pagination parameters
        const paginationParams = {
          page,
          size,
          sort,
          dir,
          search,
          showDuplicates,
          useEngApi,
          flowId
        };

        return ProcessingHubService.getUploadedDocuments(
          hasDuplicate,
          paginationParams
        );
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    // Keep the old query for backward compatibility in other components
    getUploadedDocumentsLegacy: build.query<Document[], boolean>({
      queryFn: (hasDuplicate = false) => {
        return ProcessingHubService.getUploadedDocumentsSimple(hasDuplicate);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getProcessingAssignments: build.query<any, string | undefined>({
      queryFn: (documentType?: string) => {
        return ProcessingHubService.getProcessingAssignments(documentType);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getGdxDocuments: build.query<Document[], void>({
      queryFn: () => {
        return ProcessingHubService.getGdxDocuments();
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getGdxDocumentsPaginated: build.query<
      GdxPaginatedResponse,
      {
        page: number;
        flowConfigId: string | number;
        size: number;
        sort?: string;
        dir?: string;
        search?: string;
        showDuplicates?: boolean;
      }
    >({
      queryFn: (paginationParams) => {
        return ProcessingHubService.getGdxDocumentsPaginated(paginationParams);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getFlowConfigurations: build.query<FlowConfiguration[], void>({
      queryFn: () => {
        return ProcessingHubService.getFlowConfigurations();
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getFlowConfigurationById: build.query<FlowConfiguration, string | number>({
      queryFn: (configId: string | number) => {
        return ProcessingHubService.getFlowConfigurationById(configId);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getConfigurationDetails: build.query<ConfigurationDetails, string | number>(
      {
        queryFn: (configId: string | number) => {
          return ProcessingHubService.getConfigurationDetails(configId);
        },
        providesTags: [ApiCacheTags.DOCUMENT_LIST]
      }
    ),

    getGdxDocumentDetail: build.query<
      GdxDocumentDetail,
      { documentId: string | number; flowId: string | number }
    >({
      queryFn: ({ documentId, flowId }) => {
        return ProcessingHubService.getGdxDocumentDetail(documentId, flowId);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getDocumentTypeInformation: build.query<DocumentTypeInformation[], string>({
      queryFn: (documentType: string) => {
        return ProcessingHubService.getDocumentTypeInformation(documentType);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    }),

    getBatchesPaginated: build.query<
      BatchPaginatedResponse,
      {
        page: number;
        size: number;
        sort?: string;
        dir?: string;
        flow_id: number;
      }
    >({
      queryFn: (paginationParams) => {
        return ProcessingHubService.getBatchesPaginated(paginationParams);
      },
      providesTags: [ApiCacheTags.DOCUMENT_LIST]
    })
  })
});

export const {
  useGetDocumentsQuery,
  useGetUploadedDocumentsQuery,
  useGetUploadedDocumentsLegacyQuery,
  useGetProcessingAssignmentsQuery,
  useGetGdxDocumentsQuery,
  useGetGdxDocumentsPaginatedQuery,
  useGetFlowConfigurationsQuery,
  useGetFlowConfigurationByIdQuery,
  useGetConfigurationDetailsQuery,
  useGetGdxDocumentDetailQuery,
  useGetDocumentTypeInformationQuery,
  useGetBatchesPaginatedQuery,
  useLazyGetDocumentsQuery,
  useLazyGetUploadedDocumentsQuery,
  useLazyGetGdxDocumentDetailQuery,
  useLazyGetGdxDocumentsPaginatedQuery
} = processingHubQueriesApi;
